import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
    Box,
    Paper,
    Typography,
    Tabs,
    Tab,
    List,
    ListItem,
    ListItemText,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Chip,
    Divider,
    Alert
} from '@mui/material';
import { AppGlobalContext } from '../../../App';

function TabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`change-requests-tabpanel-${index}`}
            aria-labelledby={`change-requests-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

export default function ChangeRequests_page_body() {
    const language = useSelector((state) => state.App_Settings.language);
    const userEmail = useSelector((state) => state.UserMetaData.email);
    const AppGlobalContxt = React.useContext(AppGlobalContext);
    
    const [tabValue, setTabValue] = useState(0);
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [comment, setComment] = useState('');
    const [error, setError] = useState('');

    const fetchChangeRequests = async (mode) => {
        if (!userEmail) return;
        
        setLoading(true);
        setError('');
        
        try {
            const url = window.devMode ? 'http://localhost:80/get_change_requests' : '/get_change_requests';
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: userEmail, mode })
            });
            
            const result = await response.json();
            
            if (response.ok) {
                setRequests(result.requests || []);
            } else {
                setError(result.error || 'Failed to load change requests');
            }
        } catch (err) {
            console.error('[ChangeRequests] Error fetching:', err);
            setError('Network error loading change requests');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const mode = tabValue === 0 ? 'pending' : tabValue === 1 ? 'all' : 'my_requests';
        fetchChangeRequests(mode);
    }, [tabValue, userEmail]);

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const handleOpenDialog = (request) => {
        setSelectedRequest(request);
        setDialogOpen(true);
        setComment('');
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setSelectedRequest(null);
        setComment('');
    };

    const handleApprove = async () => {
        if (!selectedRequest) return;
        
        try {
            const url = window.devMode ? 'http://localhost:80/approve_change_request' : '/approve_change_request';
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email: userEmail,
                    request_id: selectedRequest.request_id,
                    comment: comment
                })
            });
            
            const result = await response.json();
            
            if (response.ok) {
                alert('✅ Change request approved successfully!');
                handleCloseDialog();
                // Refresh the list
                const mode = tabValue === 0 ? 'pending' : tabValue === 1 ? 'all' : 'my_requests';
                fetchChangeRequests(mode);
            } else {
                alert(`❌ Error: ${result.error || 'Failed to approve'}`);
            }
        } catch (err) {
            console.error('[ChangeRequests] Error approving:', err);
            alert('❌ Network error approving change request');
        }
    };

    const handleReject = async () => {
        if (!selectedRequest || !comment) {
            alert('Please provide a reason for rejection');
            return;
        }
        
        try {
            const url = window.devMode ? 'http://localhost:80/reject_change_request' : '/reject_change_request';
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email: userEmail,
                    request_id: selectedRequest.request_id,
                    comment: comment
                })
            });
            
            const result = await response.json();
            
            if (response.ok) {
                alert('✅ Change request rejected');
                handleCloseDialog();
                // Refresh the list
                const mode = tabValue === 0 ? 'pending' : tabValue === 1 ? 'all' : 'my_requests';
                fetchChangeRequests(mode);
            } else {
                alert(`❌ Error: ${result.error || 'Failed to reject'}`);
            }
        } catch (err) {
            console.error('[ChangeRequests] Error rejecting:', err);
            alert('❌ Network error rejecting change request');
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'pending': return 'warning';
            case 'approved': return 'success';
            case 'rejected': return 'error';
            default: return 'default';
        }
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return 'N/A';
        return new Date(timestamp * 1000).toLocaleString();
    };

    return (
        <Box sx={{ width: '100%', p: 3 }}>
            <Paper sx={{ mb: 2, p: 2 }}>
                <Typography variant="h4" gutterBottom>
                    Change Requests Management
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Review and manage change requests from supervised users
                </Typography>
            </Paper>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            <Paper sx={{ width: '100%' }}>
                <Tabs value={tabValue} onChange={handleTabChange} aria-label="change request tabs">
                    <Tab label="Pending Reviews" />
                    <Tab label="All Requests" />
                    <Tab label="My Requests" />
                </Tabs>

                <TabPanel value={tabValue} index={0}>
                    {loading ? (
                        <Typography>Loading...</Typography>
                    ) : requests.length === 0 ? (
                        <Typography color="text.secondary">No pending requests</Typography>
                    ) : (
                        <List>
                            {requests.map((request) => (
                                <React.Fragment key={request.request_id}>
                                    <ListItem 
                                        alignItems="flex-start"
                                        secondaryAction={
                                            <Button 
                                                variant="outlined" 
                                                onClick={() => handleOpenDialog(request)}
                                            >
                                                Review
                                            </Button>
                                        }
                                    >
                                        <ListItemText
                                            primary={
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography variant="subtitle1">
                                                        {request.target_type === 'database' 
                                                            ? `Database: ${request.target_name}`
                                                            : `DBGroup: ${request.target_name}`}
                                                    </Typography>
                                                    <Chip 
                                                        label={request.status} 
                                                        color={getStatusColor(request.status)} 
                                                        size="small" 
                                                    />
                                                </Box>
                                            }
                                            secondary={
                                                <>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Type: {request.change_type}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Submitted: {formatDate(request.created_at)}
                                                    </Typography>
                                                    {request.target_group && (
                                                        <Typography variant="body2" color="text.secondary">
                                                            Group: {request.target_group}
                                                        </Typography>
                                                    )}
                                                </>
                                            }
                                        />
                                    </ListItem>
                                    <Divider />
                                </React.Fragment>
                            ))}
                        </List>
                    )}
                </TabPanel>

                <TabPanel value={tabValue} index={1}>
                    {loading ? (
                        <Typography>Loading...</Typography>
                    ) : requests.length === 0 ? (
                        <Typography color="text.secondary">No requests found</Typography>
                    ) : (
                        <List>
                            {requests.map((request) => (
                                <React.Fragment key={request.request_id}>
                                    <ListItem alignItems="flex-start">
                                        <ListItemText
                                            primary={
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography variant="subtitle1">
                                                        {request.target_type === 'database' 
                                                            ? `Database: ${request.target_name}`
                                                            : `DBGroup: ${request.target_name}`}
                                                    </Typography>
                                                    <Chip 
                                                        label={request.status} 
                                                        color={getStatusColor(request.status)} 
                                                        size="small" 
                                                    />
                                                </Box>
                                            }
                                            secondary={
                                                <>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Type: {request.change_type}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Submitted: {formatDate(request.created_at)}
                                                    </Typography>
                                                    {request.reviewed_at && (
                                                        <Typography variant="body2" color="text.secondary">
                                                            Reviewed: {formatDate(request.reviewed_at)}
                                                        </Typography>
                                                    )}
                                                    {request.reviewer_comment && (
                                                        <Typography variant="body2" color="text.primary">
                                                            Comment: {request.reviewer_comment}
                                                        </Typography>
                                                    )}
                                                </>
                                            }
                                        />
                                    </ListItem>
                                    <Divider />
                                </React.Fragment>
                            ))}
                        </List>
                    )}
                </TabPanel>

                <TabPanel value={tabValue} index={2}>
                    {loading ? (
                        <Typography>Loading...</Typography>
                    ) : requests.length === 0 ? (
                        <Typography color="text.secondary">You haven't submitted any requests</Typography>
                    ) : (
                        <List>
                            {requests.map((request) => (
                                <React.Fragment key={request.request_id}>
                                    <ListItem alignItems="flex-start">
                                        <ListItemText
                                            primary={
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography variant="subtitle1">
                                                        {request.target_type === 'database' 
                                                            ? `Database: ${request.target_name}`
                                                            : `DBGroup: ${request.target_name}`}
                                                    </Typography>
                                                    <Chip 
                                                        label={request.status} 
                                                        color={getStatusColor(request.status)} 
                                                        size="small" 
                                                    />
                                                </Box>
                                            }
                                            secondary={
                                                <>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Type: {request.change_type}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Submitted: {formatDate(request.created_at)}
                                                    </Typography>
                                                    {request.reviewed_at && (
                                                        <Typography variant="body2" color="text.secondary">
                                                            Reviewed: {formatDate(request.reviewed_at)}
                                                        </Typography>
                                                    )}
                                                    {request.reviewer_comment && (
                                                        <Typography variant="body2" color="text.primary">
                                                            Reviewer Comment: {request.reviewer_comment}
                                                        </Typography>
                                                    )}
                                                </>
                                            }
                                        />
                                    </ListItem>
                                    <Divider />
                                </React.Fragment>
                            ))}
                        </List>
                    )}
                </TabPanel>
            </Paper>

            {/* Review Dialog */}
            <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
                <DialogTitle>
                    Review Change Request
                </DialogTitle>
                <DialogContent>
                    {selectedRequest && (
                        <Box>
                            <Typography variant="h6" gutterBottom>
                                {selectedRequest.target_type === 'database' 
                                    ? `Database: ${selectedRequest.target_name}`
                                    : `DBGroup: ${selectedRequest.target_name}`}
                            </Typography>
                            
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                Change Type: {selectedRequest.change_type}
                            </Typography>
                            
                            {selectedRequest.target_group && (
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Group: {selectedRequest.target_group}
                                </Typography>
                            )}
                            
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                Submitted: {formatDate(selectedRequest.created_at)}
                            </Typography>
                            
                            <Divider sx={{ my: 2 }} />
                            
                            <Typography variant="subtitle2" gutterBottom>
                                Changes Preview:
                            </Typography>
                            <Paper sx={{ p: 2, bgcolor: 'grey.100', maxHeight: 300, overflow: 'auto' }}>
                                <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                                    {JSON.stringify(selectedRequest.change_payload, null, 2)}
                                </pre>
                            </Paper>
                            
                            <TextField
                                label="Comment (optional for approval, required for rejection)"
                                multiline
                                rows={3}
                                fullWidth
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                sx={{ mt: 2 }}
                            />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button onClick={handleReject} color="error" variant="outlined">
                        Reject
                    </Button>
                    <Button onClick={handleApprove} color="success" variant="contained">
                        Approve
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
